import React, { useMemo } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { json, jsonParseLinter } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { linter, lintGutter } from "@codemirror/lint";
import { EditorView } from "@codemirror/view";
import { StreamLanguage } from "@codemirror/language";
import { velocity } from "@codemirror/legacy-modes/mode/velocity";
import { useTheme } from "src/providers";
import { vscodeDarkInit, vscodeLightInit } from "@uiw/codemirror-theme-vscode";
import { Mode } from "@cloudscape-design/global-styles";

export type CodeLanguage = "json" | "markdown" | "plaintext" | "vtl";

export interface CodeEditorProps {
  value: string;
  onChange: (value: string) => void;
  language?: CodeLanguage;
  height?: string;
  placeholder?: string;
  readOnly?: boolean;
  showLineNumbers?: boolean;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
  value,
  onChange,
  language = "plaintext",
  height = "300px",
  placeholder,
  readOnly = false,
  showLineNumbers = true
}) => {
  const extensions = useMemo(() => {
    const exts = [];

    // Add language-specific extensions
    switch (language) {
      case "json":
        exts.push(json());
        exts.push(linter(jsonParseLinter()));
        // Add lint gutter to show error markers next to line numbers
        exts.push(lintGutter());
        break;
      case "markdown":
        exts.push(markdown());
        break;
      case "vtl":
        // Velocity Template Language support
        exts.push(StreamLanguage.define(velocity));
        break;
      default:
        // plaintext - no special extensions
        break;
    }

    // Add line wrapping
    exts.push(EditorView.lineWrapping);

    return exts;
  }, [language]);

  const { mode } = useTheme();
  const editorTheme = mode === Mode.Dark ? vscodeDarkInit() : vscodeLightInit();

  const basicSetup = useMemo(
    () => ({
      lineNumbers: showLineNumbers,
      highlightActiveLineGutter: true,
      highlightActiveLine: true,
      foldGutter: true,
      dropCursor: true,
      allowMultipleSelections: true,
      indentOnInput: true,
      bracketMatching: true,
      closeBrackets: true,
      autocompletion: true,
      rectangularSelection: true,
      crosshairCursor: true,
      highlightSelectionMatches: true,
      closeBracketsKeymap: true,
      searchKeymap: true,
      foldKeymap: true,
      completionKeymap: true,
      lintKeymap: true,
    }),
    [showLineNumbers]
  );

  return (
    <div className="code-editor-wrapper">
      <CodeMirror
        value={value}
        height={height}
        extensions={extensions}
        onChange={onChange}
        placeholder={placeholder}
        readOnly={readOnly}
        basicSetup={basicSetup}
        theme={editorTheme}
      />
    </div>
  );
};

export default CodeEditor;
